/**
 * CR307: Hook for loading/saving app_scan_settings singleton
 * Reads/writes the canonical S0 scan defaults for Web and Flyer scans.
 */
/* eslint-disable @typescript-eslint/no-explicit-any */

import { useState, useCallback, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface AppScanSettings {
  // Web scan defaults
  web_timeout_seconds: number;
  web_max_concurrent_scans: number;
  web_crawler_provider: string;
  web_default_recipe_template_id: string | null;
  // Flyer scan defaults
  flyer_max_pages_per_flyer: number;
  flyer_max_total_pages_per_run: number;
  // CR508: S1.4 Locator/Cropper settings
  s1_4_locator_prompt_pack_id: string | null;
  s1_4_cropper_enabled: boolean;
  s1_4_cropper_prompt_pack_id: string | null;
  // CR534: Confidence thresholds
  s1_4_min_locator_confidence: number;
  s1_4_min_cropper_confidence: number;
  // CR540: Crop area + guardrail settings
  s1_4_max_crop_area_ratio: number;
  s1_4_prefer_pass_a_when_worse: boolean;
  // CR543: OCR hint settings
  s1_4_ocr_hint_margin: number;
  s1_4_ocr_hint_max_chars: number;
  // CR690: S1.5 decode/downscale target (MP) for crop preparation
  s1_4_crop_decode_target_mp: number;
  // CR691: Oversize guard for S1.5 crop path
  s1_4_oversize_guard_enabled: boolean;
  s1_4_oversize_guard_max_mp: number;
  // CR544: Diamond Mode settings
  s1_4_run_policy: 'diamond' | 'stable';
  s1_4_recompute_policy: 'FULL' | 'COPY_FORWARD_IF_UNCHANGED' | 'LOCATE_ONLY' | 'CROP_ONLY';
  s1_4_max_attempts_kept: number;
  // CR555-P6: Cropper model override
  s1_4_cropper_model_mode: 'use_run_model' | 'override';
  s1_4_cropper_model_hint: string | null;
  // Metadata
  updated_at: string | null;
}

export const DEFAULT_SCAN_SETTINGS: AppScanSettings = {
  web_timeout_seconds: 120,
  web_max_concurrent_scans: 3,
  web_crawler_provider: 'firecrawl',
  web_default_recipe_template_id: null,
  flyer_max_pages_per_flyer: 50,
  flyer_max_total_pages_per_run: 1250,
  s1_4_locator_prompt_pack_id: null,
  s1_4_cropper_enabled: false,
  s1_4_cropper_prompt_pack_id: null,
  s1_4_min_locator_confidence: 0.80,
  s1_4_min_cropper_confidence: 0.50,
  s1_4_max_crop_area_ratio: 0.55,
  s1_4_prefer_pass_a_when_worse: true,
  s1_4_ocr_hint_margin: 0.08,
  s1_4_ocr_hint_max_chars: 500,
  s1_4_crop_decode_target_mp: 12,
  s1_4_oversize_guard_enabled: true,
  s1_4_oversize_guard_max_mp: 12,
  s1_4_run_policy: 'diamond',
  s1_4_recompute_policy: 'FULL',
  s1_4_max_attempts_kept: 10,
  s1_4_cropper_model_mode: 'use_run_model',
  s1_4_cropper_model_hint: null,
  updated_at: null,
};

export interface RecipeOption {
  id: string;
  recipe_name: string;
  is_template: boolean;
}

// ─── Hook ────────────────────────────────────────────────────────────────────

export function useScanSettings() {
  const { toast } = useToast();

  const [settings, setSettings] = useState<AppScanSettings>(DEFAULT_SCAN_SETTINGS);
  const [savedSettings, setSavedSettings] = useState<AppScanSettings>(DEFAULT_SCAN_SETTINGS);
  const [recipes, setRecipes] = useState<RecipeOption[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  // ── Load singleton + recipe options ──────────────────────────────────────

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      // Parallel fetch: settings + recipe options
      const [settingsResult, recipesResult] = await Promise.all([
        supabase
          .from('app_scan_settings')
          .select('*')
          .eq('singleton_id', true)
          .maybeSingle(),
        supabase
          .from('capture_recipe_templates')
          .select('id, recipe_name, is_template')
          .eq('recipe_scope', 'WEB_CRAWL')
          .eq('is_archived', false)
          .order('recipe_name'),
      ]);

      if (settingsResult.error) throw settingsResult.error;

      let row = settingsResult.data;

      // CR307a: Self-heal — if singleton row is missing, insert defaults
      if (!row) {
        console.warn('[useScanSettings] Singleton row missing — inserting defaults');
        const { updated_at: _, ...defaults } = DEFAULT_SCAN_SETTINGS;
        const { data: inserted, error: insertErr } = await supabase
          .from('app_scan_settings')
          .upsert({ singleton_id: true, ...defaults }, { onConflict: 'singleton_id' })
          .select('*')
          .maybeSingle();
        if (insertErr) throw insertErr;
        row = inserted;
      }

      if (row) {
        const loaded: AppScanSettings = {
          web_timeout_seconds: row.web_timeout_seconds ?? 120,
          web_max_concurrent_scans: row.web_max_concurrent_scans ?? 3,
          web_crawler_provider: row.web_crawler_provider ?? 'firecrawl',
          web_default_recipe_template_id: row.web_default_recipe_template_id ?? null,
          flyer_max_pages_per_flyer: row.flyer_max_pages_per_flyer ?? 50,
          flyer_max_total_pages_per_run: row.flyer_max_total_pages_per_run ?? 1250,
          s1_4_locator_prompt_pack_id: (row as any).s1_4_locator_prompt_pack_id ?? null,
          s1_4_cropper_enabled: (row as any).s1_4_cropper_enabled ?? false,
          s1_4_cropper_prompt_pack_id: (row as any).s1_4_cropper_prompt_pack_id ?? null,
          s1_4_min_locator_confidence: (row as any).s1_4_min_locator_confidence ?? 0.80,
          s1_4_min_cropper_confidence: (row as any).s1_4_min_cropper_confidence ?? 0.50,
          s1_4_max_crop_area_ratio: (row as any).s1_4_max_crop_area_ratio ?? 0.55,
          s1_4_prefer_pass_a_when_worse: (row as any).s1_4_prefer_pass_a_when_worse ?? true,
          s1_4_ocr_hint_margin: (row as any).s1_4_ocr_hint_margin ?? 0.08,
          s1_4_ocr_hint_max_chars: (row as any).s1_4_ocr_hint_max_chars ?? 500,
          s1_4_crop_decode_target_mp: (row as any).s1_4_crop_decode_target_mp ?? 12,
          s1_4_oversize_guard_enabled: (row as any).s1_4_oversize_guard_enabled ?? true,
          s1_4_oversize_guard_max_mp: (row as any).s1_4_oversize_guard_max_mp ?? 12,
          s1_4_run_policy: (row as any).s1_4_run_policy ?? 'diamond',
          s1_4_recompute_policy: (row as any).s1_4_recompute_policy ?? 'FULL',
          s1_4_max_attempts_kept: (row as any).s1_4_max_attempts_kept ?? 10,
          s1_4_cropper_model_mode: (row as any).s1_4_cropper_model_mode ?? 'use_run_model',
          s1_4_cropper_model_hint: (row as any).s1_4_cropper_model_hint ?? null,
          updated_at: row.updated_at ?? null,
        };
        setSettings(loaded);
        setSavedSettings(loaded);
      }

      if (!recipesResult.error && recipesResult.data) {
        setRecipes(recipesResult.data as RecipeOption[]);
      }
    } catch (err) {
      console.error('[useScanSettings] Load error:', err);
      toast({
        title: 'Failed to load scan settings',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  // ── Save ─────────────────────────────────────────────────────────────────

  const save = useCallback(async (values: Partial<AppScanSettings> = {}) => {
    const merged = { ...settings, ...values };
    setIsSaving(true);
    try {
      const { updated_at: _, ...payload } = merged;

      // CR307a: Use upsert so save works even if singleton row is missing
      const { error } = await supabase
        .from('app_scan_settings')
        .upsert({ singleton_id: true, ...payload }, { onConflict: 'singleton_id' });

      if (error) throw error;

      // Re-fetch to get server-side updated_at
      const { data: refreshed, error: refreshErr } = await supabase
        .from('app_scan_settings')
        .select('*')
        .eq('singleton_id', true)
        .maybeSingle();

      if (refreshErr) throw refreshErr;
      if (!refreshed) throw new Error('Failed to read back saved settings');

      const row = refreshed;
      const fresh: AppScanSettings = {
        web_timeout_seconds: row.web_timeout_seconds,
        web_max_concurrent_scans: row.web_max_concurrent_scans,
        web_crawler_provider: row.web_crawler_provider,
        web_default_recipe_template_id: row.web_default_recipe_template_id,
        flyer_max_pages_per_flyer: row.flyer_max_pages_per_flyer,
        flyer_max_total_pages_per_run: row.flyer_max_total_pages_per_run,
        s1_4_locator_prompt_pack_id: (row as any).s1_4_locator_prompt_pack_id ?? null,
        s1_4_cropper_enabled: (row as any).s1_4_cropper_enabled ?? false,
        s1_4_cropper_prompt_pack_id: (row as any).s1_4_cropper_prompt_pack_id ?? null,
        s1_4_min_locator_confidence: (row as any).s1_4_min_locator_confidence ?? 0.80,
        s1_4_min_cropper_confidence: (row as any).s1_4_min_cropper_confidence ?? 0.50,
        s1_4_max_crop_area_ratio: (row as any).s1_4_max_crop_area_ratio ?? 0.55,
        s1_4_prefer_pass_a_when_worse: (row as any).s1_4_prefer_pass_a_when_worse ?? true,
        s1_4_ocr_hint_margin: (row as any).s1_4_ocr_hint_margin ?? 0.08,
        s1_4_ocr_hint_max_chars: (row as any).s1_4_ocr_hint_max_chars ?? 500,
        s1_4_crop_decode_target_mp: (row as any).s1_4_crop_decode_target_mp ?? 12,
        s1_4_oversize_guard_enabled: (row as any).s1_4_oversize_guard_enabled ?? true,
        s1_4_oversize_guard_max_mp: (row as any).s1_4_oversize_guard_max_mp ?? 12,
        s1_4_run_policy: (row as any).s1_4_run_policy ?? 'diamond',
        s1_4_recompute_policy: (row as any).s1_4_recompute_policy ?? 'FULL',
        s1_4_max_attempts_kept: (row as any).s1_4_max_attempts_kept ?? 10,
        s1_4_cropper_model_mode: (row as any).s1_4_cropper_model_mode ?? 'use_run_model',
        s1_4_cropper_model_hint: (row as any).s1_4_cropper_model_hint ?? null,
        updated_at: row.updated_at,
      };
      setSettings(fresh);
      setSavedSettings(fresh);

      toast({ title: 'Scan settings saved', description: 'Defaults updated for future runs.' });
      return true;
    } catch (err) {
      console.error('[useScanSettings] Save error:', err);
      toast({
        title: 'Save failed',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [settings, toast]);

  // ── Field updater (local only — call save() to persist) ─────────────────

  const updateField = useCallback(<K extends keyof AppScanSettings>(
    key: K,
    value: AppScanSettings[K]
  ) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  }, []);

  // ── Reset ────────────────────────────────────────────────────────────────

  const reset = useCallback(() => {
    setSettings(savedSettings);
    toast({ title: 'Reset', description: 'Changes discarded.' });
  }, [savedSettings, toast]);

  // ── Dirty detection ──────────────────────────────────────────────────────

  const isDirty =
    settings.web_timeout_seconds !== savedSettings.web_timeout_seconds ||
    settings.web_max_concurrent_scans !== savedSettings.web_max_concurrent_scans ||
    settings.web_crawler_provider !== savedSettings.web_crawler_provider ||
    settings.web_default_recipe_template_id !== savedSettings.web_default_recipe_template_id ||
    settings.flyer_max_pages_per_flyer !== savedSettings.flyer_max_pages_per_flyer ||
    settings.flyer_max_total_pages_per_run !== savedSettings.flyer_max_total_pages_per_run ||
    settings.s1_4_locator_prompt_pack_id !== savedSettings.s1_4_locator_prompt_pack_id ||
    settings.s1_4_cropper_enabled !== savedSettings.s1_4_cropper_enabled ||
    settings.s1_4_cropper_prompt_pack_id !== savedSettings.s1_4_cropper_prompt_pack_id ||
    settings.s1_4_min_locator_confidence !== savedSettings.s1_4_min_locator_confidence ||
    settings.s1_4_min_cropper_confidence !== savedSettings.s1_4_min_cropper_confidence ||
    settings.s1_4_max_crop_area_ratio !== savedSettings.s1_4_max_crop_area_ratio ||
    settings.s1_4_prefer_pass_a_when_worse !== savedSettings.s1_4_prefer_pass_a_when_worse ||
    settings.s1_4_ocr_hint_margin !== savedSettings.s1_4_ocr_hint_margin ||
    settings.s1_4_ocr_hint_max_chars !== savedSettings.s1_4_ocr_hint_max_chars ||
    settings.s1_4_crop_decode_target_mp !== savedSettings.s1_4_crop_decode_target_mp ||
    settings.s1_4_oversize_guard_enabled !== savedSettings.s1_4_oversize_guard_enabled ||
    settings.s1_4_oversize_guard_max_mp !== savedSettings.s1_4_oversize_guard_max_mp ||
    settings.s1_4_run_policy !== savedSettings.s1_4_run_policy ||
    settings.s1_4_recompute_policy !== savedSettings.s1_4_recompute_policy ||
    settings.s1_4_max_attempts_kept !== savedSettings.s1_4_max_attempts_kept;

  return {
    settings,
    recipes,
    isLoading,
    isSaving,
    isDirty,
    updateField,
    save,
    reset,
    reload: load,
  };
}
